import { FC, useEffect, useState } from 'react';
import { Modal } from '@zeeve-platform/modal-component';
import { Inputbox } from '@zeeve-platform/inputbox-component';
import { Breadcrumb } from '@zeeve-platform/breadcrumb-component';
import Switch from 'react-switch';
import axios from 'axios';

import useSidebarStore from '../../../store/sidebar';
import BaseUrls from '../../../utils/baseUrls';
import { NetworkType, RpcDetailType } from '../../../types/network';
import Loader from '../../../components/Loader';
import {
    getNetworkDetail as getNetworkDetailData,
    getAllNodes as getAllNodesData,
    deleteNetwork as deleteTheNetwork,
    getBlockHeight as getBlockHeightData,
} from '../../../services/network';
import { breadcrumbPath, prefixPath } from '../../../utils/helpers';
import sync from '../../../assets/sync.png';
import shardeumLogo from '../../../assets/shardeum_dark.svg';
import ServerCard from './ServerCard';
// import BorApiCheckList from './BorApiCheckList';
import NodeCard from './NodeCard';

interface NetworkDetailsProps {
    id: string;
}

type CodeSnippetType = 'CURL' | 'WSCAT' | 'NodeJs' | 'Python' | 'Go' | 'none';

const NetworkDetails: FC<NetworkDetailsProps> = ({ id }) => {
    const [networkData, setNetworkData] = useState<NetworkType[] | []>([]);
    const [rpcDetail, setRpcDetail] = useState({} as RpcDetailType);
    const [flag, setFlag] = useState<boolean>(false);
    const [page, setPage] = useState<string>('node');
    const [name, setName] = useState<string>('My Network');
    const [networkPayment, setNetworkPayment] = useState<boolean>(false);
    const [redirectionUrl, setRedirectionUrl] = useState<string>('');
    const [nodeId, setNodeId] = useState<string>('');
    const [mangedHosting, setManagedHosting] = useState<boolean>(false);
    const [networkStatus, setNetworkStatus] = useState<string>('');
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [modalShow, setModalShow] = useState<boolean>(false);
    const [deleteNetworkMsg, setDeleteNetworkMsg] = useState<string>('');
    const [networkType, setNetworkType] = useState<'dedicated' | 'staking'>('dedicated');
    // const SyncMode: string[] = ['Fast', 'Full'];

    const [codeSnippet, setCodeSnippet] = useState<CodeSnippetType>('CURL');
    const [codeType, setCodeType] = useState<'https' | 'wss'>('https');
    const [codes, setCodes] = useState<{ lang: CodeSnippetType; http?: string; wss?: string }[]>([]);

    const [isNodeStatusRequested, setIsNodeStatusRequested] = useState(false);

    useEffect(() => {
        if (networkType === 'staking' && networkData.length != 0) {
            setNodeId(networkData[0].node_id.toString());
        }
    }, [networkData, networkType]);

    /** get value of 'from' key to set breadcrumb path */
    const from = localStorage.getItem('from') || '';
    /** returns breadcrumb path */
    const paths = breadcrumbPath(from);

    function myFunction() {
        document.getElementById('my-dropdown')?.classList.toggle('show');
    }

    window.onclick = function (event) {
        const drop = document.getElementById('action-btn');
        if (event.target !== drop) {
            const dropdowns = document.getElementsByClassName('dropdown-content');
            let i;
            for (i = 0; i < dropdowns.length; i += 1) {
                const openDropdown = dropdowns[i];
                if (openDropdown.classList.contains('show')) {
                    openDropdown.classList.remove('show');
                }
            }
        }
    };

    const getNetworkDetail = async () => {
        setShowLoader(true);
        const response: any = await getNetworkDetailData(id);
        // const response: any = await (await axios.get(`http://localhost:3001/networks/${id}`)).data;
        setShowLoader(false);
        if (response && response.success) {
            setNetworkPayment(response.data.status === 'payment_pending');
            setName(response.data.name);
            setNetworkType(response.data.networkNodeType);
            setRedirectionUrl(response.data.redirectionUrl);
            setManagedHosting(response.data.inputConfigurations.managedHosting);
            setNetworkStatus(response.data.status);
        }
    };

    const getAllNodes = async () => {
        setShowLoader(true);
        const response: any = await getAllNodesData(id);
        // const response: any = await (await axios.get(`http://localhost:3001/networks/${id}/nodes`)).data;
        setShowLoader(false);
        if (response && response.success) {
            setNetworkData(response.data);
        } else {
            setNetworkData([]);
        }
    };

    useEffect(() => {
        // Call api for get network details
        getNetworkDetail();
        // Call api for get all nodes list
        getAllNodes();
    }, []);

    const deleteNetwork = async (networkId: number | string) => {
        setShowLoader(true);
        try {
            const response: any = await deleteTheNetwork(networkId);
            setShowLoader(false);
            if (response) {
                if (response.success) {
                    setDeleteNetworkMsg(response.data.message);
                    window.open(BaseUrls.getPlatformDashboardUrl(), '_self');
                } else if (response.error) {
                    setDeleteNetworkMsg('Error deleting network');

                    if (response.error.message) setDeleteNetworkMsg(response.error.message);
                    if (response.error.reason) setDeleteNetworkMsg(response.error.reason);
                    if (response.error.details) setDeleteNetworkMsg(response.error.details);
                }
            } else {
                setDeleteNetworkMsg('Error deleting network');
            }
        } catch (err: any) {
            setShowLoader(false);
            setDeleteNetworkMsg('Error deleting network');

            const { error } = err.response.data;

            if (error) {
                if (error.reason) setDeleteNetworkMsg(error.reason);
                if (error.details) setDeleteNetworkMsg(error.details);
            }
        }
    };

    const checkNetworkStatus = (): boolean => {
        if (networkStatus === 'ready' || networkStatus === 'provisioning') {
            return true;
        }
        return false;
    };

    const setSelected = (num: number) => {
        for (let i = 1; i < 4; i += 1) {
            const spanSelected: HTMLBaseElement | null = document.querySelector(`#span${i}`);
            const liSelected: HTMLBaseElement | null = document.querySelector(`#li${i}`);
            if (i !== num) {
                if (spanSelected && liSelected && spanSelected.classList.contains('active-node')) {
                    spanSelected.classList.remove('active-node');
                    liSelected.classList.remove('active-item');
                }
            } else if (i === num && spanSelected && !spanSelected.classList.contains('active-node')) {
                spanSelected.classList.add('active-node');
                liSelected?.classList.add('active-item');
            }
        }
    };

    const setView = () => {
        if (page === 'node') {
            return (
                <NodeCard
                    networkData={networkData}
                    setRpcDetail={setRpcDetail}
                    setFlag={setFlag}
                    networkPayment={networkPayment}
                    setNodeId={setNodeId}
                    nodeID={nodeId}
                    setCodes={setCodes}
                    networkType={networkType}
                    syncBlockHeight={blockHeightCall}
                    managedHosting={mangedHosting}
                />
            );
        }
        return null;
    };

    const copyText = (val: string) => {
        const ptag: HTMLElement | null = document.getElementById('succ');
        const ptag2: HTMLElement | null = document.getElementById(val);

        if (ptag2) {
            const txt = ptag2.textContent;
            if (txt) {
                navigator.clipboard.writeText(txt);
                if (ptag) {
                    ptag.style.display = 'block';
                    setTimeout(() => {
                        ptag.style.display = 'none';
                    }, 1500);
                }
            }
        }
    };

    const blockHeightCall = () => {
        if (networkStatus !== 'provisioning' && networkStatus !== 'ready') return;

        if (networkType === 'dedicated') {
            if (flag) {
                // getBlockHeight();
            }
        } else {
            // getBlockHeight();
        }
    };

    useEffect(() => {
        blockHeightCall();
    }, [nodeId]);

    useEffect(() => {
        const requested = networkData.some((data) => data.status === 'requested');
        setIsNodeStatusRequested(requested);
      }, [networkData]);

    const getBlockHeight = async () => {
        const blockHeightTag = document.getElementById('blockHeight');
        const stakingBlockStatusTag = document.getElementById('staking-block-status');
        const stakingBlockHeightTag = document.getElementById('staking-block-height');

        setShowLoader(true);
        const response: any = await getBlockHeightData(id.toString(), nodeId.toString());
        // const response: any = await (await axios(`http://localhost:3001/networks/${id}/nodes/${nodeId}/status`)).data;
        setShowLoader(false);
        if (response && response.success) {
            if (blockHeightTag) {
                blockHeightTag.innerHTML = response.data.blockHeight;
            }

            if (networkType === 'staking') {
                if (response.data.sync) {
                    if (stakingBlockStatusTag) stakingBlockStatusTag.innerHTML = 'Syncing';
                } else {
                    if (stakingBlockStatusTag) stakingBlockStatusTag.innerHTML = 'Not Syncing';
                }

                if (response.data.blockHeight) {
                    if (stakingBlockHeightTag) stakingBlockHeightTag.innerHTML = response.data.blockHeight;
                } else {
                    if (stakingBlockHeightTag) stakingBlockHeightTag.innerHTML = '0';
                }
            }
        }

        if (response && response.error) {
            if (stakingBlockHeightTag) stakingBlockHeightTag.innerHTML = 'Not syncing';
        }
    };


    return (
        <section className='px-5 col-12'>
            <div>
                <div className='col-12 mt-4 mx-auto'>
                    <img src={shardeumLogo} alt="shardeum-logo" width='130px' />
                    <div className="row align-items-center">
                        <div className='col-lg-6 mt-2 col-xl-6 col-sm-12 col-xs-12'>
                            <Breadcrumb prefixLink={paths} className='mt-1' textSize={14} activeLink={name || 'loading'}>
                                <span className='p-1 mx-2 all-color' style={{ fontSize: '13px' }}>
                                    {mangedHosting ? ' - Zeeve Managed' : ' - BYOC'}
                                </span>
                            </Breadcrumb>
                        </div>
                        <div className='col-lg-6 col-xl-6 col-sm-12 col-xs-12 button-add'>
                            <div className='d-flex justify-content-end mx-5' style={{}}>
                                <div className='dropdown cursor'>
                                    <button
                                        type='button'
                                        onClick={() => {
                                            myFunction();
                                        }}
                                        disabled={!checkNetworkStatus()}
                                        id='action-btn'
                                        className='ms-2 btn btn-primary'
                                        style={{ borderRadius: '10px' }}
                                    >
                                        Actions
                                    </button>
                                    <div id='my-dropdown' className='dropdown-content'>
                                        {networkType === 'dedicated' && (
                                            <li
                                                aria-hidden
                                                onClick={() => {
                                                    window.open(prefixPath(`/nodes/full/${id}/add`), '_self');
                                                }}
                                            >
                                                Add Node
                                            </li>
                                        )}

                                        {/* {networkType === 'staking' && (
                                            <li
                                                aria-hidden
                                                onClick={() => {
                                                    window.open(prefixPath(`/nodes/validator/${id}/add`), '_self');
                                                }}
                                            >
                                                Add Staking Node
                                            </li>
                                        )} */}

                                        <li
                                            aria-hidden
                                            onClick={() => {
                                                setModalShow(true);
                                            }}
                                        >
                                            Delete Network
                                        </li>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='alert alert-danger mb-1' id='fail' style={{ display: 'none' }} role='alert'>
                        User currently doesn&apos;t have permission to perform this action
                    </div>
                    <div className='alert alert-danger mb-1' id='analyticsDiv' style={{ display: 'none' }} role='alert'>
                        Analytics will start after node is in provisioning state
                    </div>
                    <div id='actionSuccess' className='alert alert-success mb-1' style={{ display: 'none' }} role='alert'>
                        Action Performed
                    </div>
                    <div className='alert alert-danger mb-1' id='actionFail' style={{ display: 'none' }} role='alert'>
                        Error performing action
                    </div>
                </div>
                {networkType === 'dedicated' && (
                    <div className='row justify-content-between '>
                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12 mt-5'>
                            <div className='card node-process h-100'>
                                <nav className='navbar navbar-expand-lg navbar-light border-bottom'>
                                    <div className='container-fluid'>
                                        <div className='' id='navbarNav'>
                                            <ul className='Navbar nav container-equal-space'>
                                                <li
                                                    id='Li1'
                                                    className='nav-item active-item'
                                                    aria-hidden
                                                    onClick={() => {
                                                        setPage('node');
                                                        setSelected(1);
                                                    }}
                                                >
                                                    <span id='span1' className='nav-link active-node '>
                                                        Node(s)
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </nav>
                                <div>{setView()}</div>
                            </div>
                        </div>
                        <ServerCard data={networkData} />
                    </div>
                )}
                {networkType === 'staking' && (
                    <div className='row justify-content-between '>
                        <div className='col-12 mt-5 h-50'>
                            <div className='card node-process h-50'>
                                <nav className='navbar navbar-expand-lg navbar-light border-bottom'>
                                    <div className='container-fluid'>
                                        <div className='' id='navbarNav'>
                                            <ul className='Navbar nav container-equal-space'>
                                                <li
                                                    id='Li1'
                                                    className='nav-item active-item'
                                                    aria-hidden
                                                    onClick={() => {
                                                        setPage('node');
                                                        setSelected(1);
                                                    }}
                                                >
                                                    <span id='span1' className='nav-link active-node '>
                                                        Node(s)
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </nav>
                                <table className='table table-responsive'>
                                    <thead className='text-center'>
                                        <tr>
                                            <th scope='col'>Node Name</th>
                                            <th scope='col'>Cloud Region</th>
                                            <th scope='col'>Network Type</th>
                                            <th scope='col'>Created At</th>
                                            {/* <th scope='col'>Block Height Status</th> */}
                                            <th scope='col'>Node Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>{setView()}</tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
                {showLoader && <Loader style={{ zIndex: 100 }} />}
            </div>
            <div id='succ' className='alert alert-success mt-3 mb-5' style={{ display: 'none' }} role='alert'>
                Copied
            </div>
            {networkType === 'dedicated' && (
                <div className='row '>
                    <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12 mx-auto mt-5'>
                        <div className='card infoCard h-100'>
                            <div
                                className='m-4 border p-4'
                                style={{ backgroundColor: '#F7F7F7', borderRadius: '10px' }}
                            >
                                <h6 className='mt-3 text-center'>Node Info</h6>
                                <div
                                    className='row mb-2 mt-3 text-center col-12 ms-1'
                                    style={{ border: '2px solid #D7DBE3', borderRadius: '5px' }}
                                >
                                    <div className='col-4 pt-3  border-end'>BlockHeight</div>
                                    <div className='col-4 pt-3 border-end'>
                                        <p id='blockHeight'>0</p>
                                    </div>
                                    <div className='col-4 mt-3 text-center'>
                                        <li
                                            className='btnNodeli col-3 mx-5'
                                            style={{ border: '1px solid #D7DBE3' }}
                                            aria-hidden
                                            title={nodeId ? 'refresh' : 'please select node first'}
                                            onClick={() => {
                                                blockHeightCall();
                                            }}
                                        >
                                            <img className='btnNode' src={sync} height='22px' alt='' />
                                        </li>
                                    </div>
                                </div>
                                <h6 className='mt-4 text-center'>Endpoints</h6>
                                <p className='mt-3 text-muted'>HTTP</p>
                                <div className='mt-1 mb-3 d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mt-3 border bg-white cursor'>
                                    <p
                                        id='http-ptag'
                                        className=' mb-0 text-muted'
                                        style={{
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        Please Select your node.
                                    </p>
                                    <i
                                        className='fa fa-copy fs-6 text-primary'
                                        aria-hidden
                                        onClick={() => {
                                            copyText('http-ptag');
                                        }}
                                    />
                                </div>
                                <p className='mt-3 text-muted'>Web Socket</p>

                                <div className='mt-1 mb-3 d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mt-3 border bg-white cursor'>
                                    <p
                                        id='web-tag'
                                        className='mb-0 text-muted'
                                        style={{
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        Please Select your node.
                                    </p>
                                    <i
                                        className='fa fa-copy fs-6 text-primary'
                                        aria-hidden
                                        onClick={() => {
                                            copyText('web-tag');
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div id='display' className='col-xl-6 col-md-12 col-sm-12 col-lg-6 mx-auto mt-5'>
                        <div className='card h-100 infoCard'>
                            <div
                                className='m-4 p-4 infoCard'
                                style={{ backgroundColor: '#F7F7F7', borderRadius: '10px' }}
                            >
                                <h6 className='mt-1 text-center'>Example Connection Snippet</h6>
                                {/* <h6 className="mt-3 text-end mb-3 text-primary">Js</h6> */}
                                {/* <div
              className="mt-3 card rounded-3"
              style={{
                overflowX: 'scroll',
              }}
            >
              <p
                className="mt-3 text-muted"
                id="url-tag"
                style={{ paddingLeft: '2%', textAlign: 'left' }}
              >
                const url = select your node ;
              </p>
              <p
                className="mt-2 text-muted mb-3"
                style={{ paddingLeft: '2%', textAlign: 'left' }}
              >
                {' // Using web3js '}
                <br />
                const web3 = new Web3(url);
                <br />
                <br />
                {' // Using ethers.js '}
                <br />
                const provider = new ethers.providers.JsonRpcProvider(url);
              </p>
            </div> */}
                                <p className='text-end mt-2 mb-2'>
                                    <select
                                        className='form-select'
                                        defaultValue='Curl'
                                        onChange={(event) => {
                                            const selected = event.currentTarget.value as CodeSnippetType;
                                            if (selected === 'WSCAT') setCodeType('wss');
                                            else setCodeType('https');
                                            setCodeSnippet(selected);
                                        }}
                                    >
                                        {codes.length === 0 && <option selected>Please select your node</option>}
                                        {codes.map((code, index) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <option key={index} value={code.lang}>
                                                {code.lang}
                                            </option>
                                        ))}
                                    </select>
                                </p>
                                <div className='card p-2 endpointcard rounded-2 bg-light'>
                                    <ul className='nav nav-tabs ' id='myTab' role='tablist'>
                                        <li
                                            className='nav-item'
                                            role='presentation'
                                            style={{
                                                display: codeSnippet === 'WSCAT' ? 'none' : 'block',
                                            }}
                                            onClick={() => setCodeType('https')}
                                        >
                                            <button
                                                className={`nav-link px-4 ${codeType === 'https' ? 'active' : ''}`}
                                                data-bs-toggle='tab'
                                                type='button'
                                                role='tab'
                                            >
                                                <span className='p-1'>HTTP</span>
                                            </button>
                                        </li>
                                        <li
                                            className='nav-item'
                                            role='presentation'
                                            style={{
                                                display: codeSnippet === 'CURL' ? 'none' : 'block',
                                            }}
                                            onClick={() => setCodeType('wss')}
                                        >
                                            <button
                                                className={`nav-link px-4 ${codeType === 'wss' ? 'active' : ''}`}
                                                data-bs-toggle='tab'
                                                type='button'
                                                role='tab'
                                            >
                                                <span className='p-1'>WS</span>
                                            </button>
                                        </li>
                                    </ul>
                                    <div className='d-flex justify-content-between gap-2'>
                                        <pre>
                                            <code id='codesnippet' className=''>
                                                {codes.length === 0 && <>Please select your node</>}
                                                {codes.length > 0 &&
                                                    codeType === 'https' &&
                                                    (codes.find((code) => codeSnippet === code.lang)?.http || (
                                                        <>HTTP endpoint is not available!</>
                                                    ))}

                                                {codes.length > 0 &&
                                                    codeType === 'wss' &&
                                                    (codes.find((code) => codeSnippet === code.lang)?.wss || (
                                                        <>WebSocket endpoint is not available!</>
                                                    ))}
                                            </code>
                                        </pre>
                                        <span>
                                            <i
                                                aria-hidden
                                                id="codesnippetcopy"
                                                className='fa fa-copy fs-6 text-primary p-2 cursor'
                                                onClick={() => {
                                                    copyText('codesnippet');
                                                }}
                                            />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {networkType === 'staking' &&
                networkData.map((node) => {
                    if (node.node_id === nodeId) {
                        return (
                            <>
                                {(node.inputs.http || node.inputs.ws || node.endpoint) && (
                                    <div className='row '>
                                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12 mx-auto mt-5'>
                                            <div className='card infoCard h-100'>
                                                <div
                                                    className='m-4 border p-4'
                                                    style={{ backgroundColor: '#F7F7F7', borderRadius: '10px' }}
                                                >
                                                    <h6 className='mt-1 text-center'>Endpoints</h6>
                                                    <p className='mt-3 text-muted'>HTTP</p>
                                                    <div className='mt-1 mb-3 d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mt-3 border bg-white'>
                                                        <p
                                                            id='http-ptag'
                                                            className=' mb-0 text-muted'
                                                            style={{
                                                                wordBreak: 'break-all',
                                                            }}
                                                        >
                                                            Please Select your node.
                                                        </p>
                                                        <i
                                                            className='fa fa-copy fs-6 text-primary'
                                                            aria-hidden
                                                            onClick={() => {
                                                                copyText('http-ptag');
                                                            }}
                                                        />
                                                    </div>
                                                    <p className='mt-3 text-muted'>Web Socket</p>

                                                    <div className='mt-1 mb-3 d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mt-3 border bg-white'>
                                                        <p
                                                            id='web-tag'
                                                            className='mb-0 text-muted'
                                                            style={{
                                                                wordBreak: 'break-all',
                                                            }}
                                                        >
                                                            Please Select your node.
                                                        </p>
                                                        <i
                                                            className='fa fa-copy fs-6 text-primary'
                                                            aria-hidden
                                                            onClick={() => {
                                                                copyText('web-tag');
                                                            }}
                                                        />
                                                    </div>
                                                    <p className='mt-3 text-muted'>Dashboard</p>
                                                    <div className='mt-1 mb-3 d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mt-3 border bg-white cursor'>
                                                        <a
                                                            id='dashboard-ptag'
                                                            className=' mb-0 text-muted'
                                                            style={{
                                                                wordBreak: 'break-all',
                                                            }}
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                        >
                                                            Please Select your node.
                                                        </a>
                                                        <i
                                                            className='fa fa-copy fs-6 text-primary'
                                                            aria-hidden
                                                            onClick={() => {
                                                                copyText('dashboard-ptag');
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            id='display'
                                            className='col-xl-6 col-md-12 col-sm-12 col-lg-6 mx-auto mt-5'
                                        >
                                            <div className='card h-100 infoCard'>
                                                <div
                                                    className='m-4 p-4 infoCard'
                                                    style={{ backgroundColor: '#F7F7F7', borderRadius: '10px' }}
                                                >
                                                    <h6 className='mt-1 text-center'>Example Connection Snippet</h6>
                                                    {/* <h6 className="mt-3 text-end mb-3 text-primary">Js</h6> */}
                                                    {/* <div
                      className="mt-3 card rounded-3"
                      style={{
                        overflowX: 'scroll',
                      }}
                    >
                      <p
                        className="mt-3 text-muted"
                        id="url-tag"
                        style={{ paddingLeft: '2%', textAlign: 'left' }}
                      >
                        const url = select your node ;
                      </p>
                      <p
                        className="mt-2 text-muted mb-3"
                        style={{ paddingLeft: '2%', textAlign: 'left' }}
                      >
                        {' // Using web3js '}
                        <br />
                        const web3 = new Web3(url);
                        <br />
                        <br />
                        {' // Using ethers.js '}
                        <br />
                        const provider = new ethers.providers.JsonRpcProvider(url);
                      </p>
                    </div> */}
                                                    <p className='text-end mt-2 mb-2'>
                                                        <select
                                                            className='form-select'
                                                            defaultValue='Curl'
                                                            onChange={(event) => {
                                                                const selected = event.currentTarget
                                                                    .value as CodeSnippetType;
                                                                if (selected === 'WSCAT') setCodeType('wss');
                                                                else setCodeType('https');
                                                                setCodeSnippet(selected);
                                                            }}
                                                        >
                                                            {codes.length === 0 && (
                                                                <option selected>Please select your node</option>
                                                            )}
                                                            {codes.map((code, index) => (
                                                                // eslint-disable-next-line react/no-array-index-key
                                                                <option key={index} value={code.lang}>
                                                                    {code.lang}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </p>
                                                    <div className='card p-2 endpointcard rounded-2 bg-light'>
                                                        <ul className='nav nav-tabs ' id='myTab' role='tablist'>
                                                            <li
                                                                className='nav-item'
                                                                role='presentation'
                                                                style={{
                                                                    display: codeSnippet === 'WSCAT' ? 'none' : 'block',
                                                                }}
                                                                onClick={() => setCodeType('https')}
                                                            >
                                                                <button
                                                                    className={`nav-link px-4 ${
                                                                        codeType === 'https' ? 'active' : ''
                                                                    }`}
                                                                    data-bs-toggle='tab'
                                                                    type='button'
                                                                    role='tab'
                                                                >
                                                                    <span className='p-1'>HTTP</span>
                                                                </button>
                                                            </li>
                                                            <li
                                                                className='nav-item'
                                                                role='presentation'
                                                                style={{
                                                                    display: codeSnippet === 'CURL' ? 'none' : 'block',
                                                                }}
                                                                onClick={() => setCodeType('wss')}
                                                            >
                                                                <button
                                                                    className={`nav-link px-4 ${
                                                                        codeType === 'wss' ? 'active' : ''
                                                                    }`}
                                                                    data-bs-toggle='tab'
                                                                    type='button'
                                                                    role='tab'
                                                                >
                                                                    <span className='p-1'>WS</span>
                                                                </button>
                                                            </li>
                                                        </ul>
                                                        <div className='d-flex justify-content-between gap-2'>
                                                            <pre>
                                                                <code id='codesnippet' className=''>
                                                                    {codes.length === 0 && <>Please select your node</>}
                                                                    {codes.length > 0 &&
                                                                        codeType === 'https' &&
                                                                        (codes.find((code) => codeSnippet === code.lang)
                                                                            ?.http || (
                                                                            <>HTTP endpoint is not available!</>
                                                                        ))}

                                                                    {codes.length > 0 &&
                                                                        codeType === 'wss' &&
                                                                        (codes.find((code) => codeSnippet === code.lang)
                                                                            ?.wss || (
                                                                            <>WebSocket endpoint is not available!</>
                                                                        ))}
                                                                </code>
                                                            </pre>
                                                            <span>
                                                                <i
                                                                    aria-hidden
                                                                    className='fa fa-copy fs-6 text-primary p-2 cursor'
                                                                    id="codesnippetcopy"
                                                                    onClick={() => {
                                                                        copyText('codesnippet');
                                                                    }}
                                                                />
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div
                                    key={node.node_id}
                                    id=''
                                    className='col-xl-12 col-lg-11 col-md-12 col-sm-12 my-5 node-process p-1 d-none'
                                    style={{
                                        display: 'flex',
                                        height: 'fit-content',
                                    }}
                                >
                                    <div
                                        className='m-4 p-4 border'
                                        style={{
                                            backgroundColor: '#F7F7F7',
                                            borderRadius: '10px',
                                            width: '97%',
                                        }}
                                    >

                                        <hr className='mx-2 my-4' />
                                        
                                    </div>
                                </div>
                            </>
                        );
                    }
                })}
            <Modal
                show={modalShow}
                onHide={() => setModalShow(false)}
                title={!isNodeStatusRequested ? `Are you sure you want to delete ${name} Network ?` : ''}
                visibleFooter
                visibleFooterCloseButton
                visibleHeaderCloseIcon={false}
                visibleConfirmBtn={!isNodeStatusRequested}
                confirmBtnText='Yes'
                onConfirm={() => deleteNetwork(id as any)}
                confirmBtnClassName=''
            >
                {isNodeStatusRequested ? (
                    <div className='alert alert-danger text-center mt-3' role='alert'>
                        <h6 className='ms-1 mb-0 col-12'>
                           Network cannot be deleted right now as at least one of the node is still not in provisioning or ready state !
                        </h6>
                    </div>
                ) : (
                    <p className='status text-center pt-3'>{deleteNetworkMsg}</p>
                )}
            </Modal>
        </section>
    );
};

export default NetworkDetails;
